<template>
  <aside class="sidebar">
    <div class="sidebar-inner">
      <router-link to="/" class="sidebar-home">Events</router-link>
      <router-link v-if="canSeeSetup" :to="setupPath" class="sidebar-link" :class="{ active: isSetup }">Setup</router-link>
      <router-link v-if="canSeeParticipants" :to="participantsPath" class="sidebar-link" :class="{ active: isParticipants }">Participants</router-link>
      <router-link v-if="canSeeOratorPrep" :to="oratorPrepPath" class="sidebar-link" :class="{ active: isOratorPrep }">Orator Prep</router-link>
      <router-link v-if="canSeeReview" :to="reviewPath" class="sidebar-link" :class="{ active: isReview }">Pronunciation Review</router-link>
      <router-link v-if="canSeeMessages" :to="messagesPath" class="sidebar-link">Messages</router-link>
      <router-link v-if="canSeeExports" :to="exportsPath" class="sidebar-link" :class="{ active: isExports }">Exports</router-link>
      <router-link v-if="canSeeViews" :to="viewsPath" class="sidebar-link" :class="{ active: isViews }">View Settings</router-link>
      <router-link v-if="canSeeQueue" :to="queuePath" class="sidebar-link" :class="{ active: isQueue }">Queue</router-link>
      <router-link v-if="canSeeQueue" :to="devicesPath" class="sidebar-link" :class="{ active: isDevices }">Devices</router-link>
      <router-link v-if="canSeePresenter" :to="presenterPath" class="sidebar-link" :class="{ active: isPresenter }">Presenter</router-link>
      <router-link v-if="canSeeLowerThirds" :to="lowerThirdsPath" class="sidebar-link" :class="{ active: isLowerThirds }">Lower Thirds</router-link>
    </div>
  </aside>
</template>

<script>
import { canManageMessages, hasAnyRole } from "@/lib/auth";

export default {
  name: "EventSidebar",
  props: {
    eventId: {
      type: String,
      required: true,
    },
  },
  computed: {
    canSeeMessages() {
      return canManageMessages();
    },
    messagesPath() {
      return { path: "/manage/messages", query: { eventId: this.eventId } };
    },
    canSeeSetup() {
      return hasAnyRole(["internal_admin", "uni_admin"]);
    },
    canSeeParticipants() {
      return hasAnyRole(["internal_admin", "uni_admin", "operator"]);
    },
    canSeeOratorPrep() {
      return hasAnyRole(["internal_admin", "uni_admin", "orator"]);
    },
    canSeeReview() {
      return hasAnyRole(["internal_admin", "uni_admin"]);
    },
    canSeeExports() {
      return hasAnyRole(["internal_admin", "uni_admin"]);
    },
    canSeeViews() {
      return hasAnyRole(["internal_admin", "uni_admin", "av"]);
    },
    canSeeQueue() {
      return hasAnyRole(["internal_admin", "uni_admin", "operator"]);
    },
    canSeePresenter() {
      return hasAnyRole(["internal_admin", "uni_admin", "operator", "orator"]);
    },
    canSeeLowerThirds() {
      return hasAnyRole(["internal_admin", "uni_admin", "operator", "av"]);
    },
    setupPath() {
      return `/events/${encodeURIComponent(this.eventId)}/setup`;
    },
    participantsPath() {
      return `/events/${encodeURIComponent(this.eventId)}/participants`;
    },
    oratorPrepPath() {
      return `/events/${encodeURIComponent(this.eventId)}/orator-prep`;
    },
    reviewPath() {
      return `/events/${encodeURIComponent(this.eventId)}/review`;
    },
    exportsPath() {
      return `/events/${encodeURIComponent(this.eventId)}/exports`;
    },
    viewsPath() {
      return `/events/${encodeURIComponent(this.eventId)}/views`;
    },
    queuePath() {
      return `/events/${encodeURIComponent(this.eventId)}/queue`;
    },
    presenterPath() {
      return `/events/${encodeURIComponent(this.eventId)}/presenter`;
    },
    devicesPath() {
      return `/events/${encodeURIComponent(this.eventId)}/devices`;
    },
    isDevices() {
      return this.$route.path.endsWith("/devices");
    },
    lowerThirdsPath() {
      return `/events/${encodeURIComponent(this.eventId)}/lower-thirds`;
    },
    isSetup() {
      return this.$route.path.endsWith("/setup");
    },
    isParticipants() {
      return this.$route.path.endsWith("/participants");
    },
    isOratorPrep() {
      return this.$route.path.endsWith("/orator-prep");
    },
    isReview() {
      return this.$route.path.endsWith("/review");
    },
    isExports() {
      return this.$route.path.endsWith("/exports");
    },
    isViews() {
      return this.$route.path.endsWith("/views");
    },
    isQueue() {
      return this.$route.path.endsWith("/queue") || this.$route.path.endsWith("/manage");
    },
    isPresenter() {
      return this.$route.path.endsWith("/presenter");
    },
    isLowerThirds() {
      return this.$route.path.endsWith("/lower-thirds");
    },
  },
};
</script>

<style scoped>
.sidebar {
  width: 220px;
  flex: 0 0 220px;
}

.sidebar-inner {
  background: white;
  border: 1px solid var(--orator-smoke);
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.06);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.sidebar-home,
.sidebar-link {
  display: block;
  padding: 0.75rem 0.9rem;
  border-radius: 0.5rem;
  text-decoration: none;
  font-weight: 600;
  color: var(--orator-arsenic);
}

.sidebar-home {
  margin-bottom: 0.5rem;
}

.sidebar-link.active {
  background: var(--orator-blue);
  color: white;
}

.sidebar-link:not(.active):hover,
.sidebar-home:hover {
  background: var(--orator-cloud);
}

@media (max-width: 960px) {
  .sidebar {
    width: 100%;
    flex-basis: 100%;
  }
}
</style>
